// GA4 이벤트 사전 — 설계.md §7.2/§7.3 과 1:1.
// 새 이벤트/파라미터는 반드시 여기 먼저 등록한다 (PII 검토 필수). 등록되지 않은 키는 전송되지 않는다.
export type AnalyticsEventName =
  | 'page_view'
  | 'login'
  | 'logout'
  | 'session_expired'
  | 'seller_switch'
  | 'access_denied'
  | 'api_error'
  | 'menu_click'
  | 'ui_click'
  | 'outbound_click'
  | 'product_create'
  | 'product_update'
  | 'product_soldout_apply'
  | 'product_search'
  | 'product_bulk_edit'
  | 'price_stock_update'
  | 'option_editor_apply'
  | 'order_search'
  | 'order_action'
  | 'invoice_register'
  | 'coupon_form_view'
  | 'coupon_create'
  | 'coupon_validation_fail'
  | 'coupon_end'
  | 'campaign_apply_start'
  | 'campaign_select'
  | 'campaign_apply'
  | 'qna_answer'
  | 'settings_update'
  | 'excel_download'

export type AnalyticsParams = Record<string, string | number | undefined>

// 검증 컨텍스트 — 라우터 등록 템플릿 Set 과 허용 라벨 Set 은 index.ts 가 채운다
export type SanitizeCtx = { routePatterns: Set<string>; labels: Set<string> }

// 규칙 종류: enum(허용값) / re(정규식) / int(정수) / label(절단만 — 정적 문자열 전용) / route(라우터 Set) / menuLabel(라벨 Set)
type Rule = {
  enum?: readonly string[]
  re?: RegExp
  int?: boolean
  label?: boolean
  route?: boolean
  extra?: readonly string[] // route 규칙에서 추가로 허용하는 고정값
  menuLabel?: boolean
}

const MAX_LEN = 100
const CODE_RE = /^[A-Za-z0-9_.:-]{1,40}$/
const NO_RE = /^\d{1,12}$/
const HOST_RE = /^[a-z0-9.-]{1,100}$/i
const URL_RE = /^https?:\/\/[^\s?#]{1,200}$/
const URL_OR_EMPTY_RE = /^(https?:\/\/[^\s?#]{1,200})?$/
const ENDPOINT_RE = /^[A-Za-z0-9_:/.-]{1,100}$/

const RESULT = ['success', 'partial', 'fail'] as const
const YN = ['Y', 'N'] as const
const BUCKETS = ['0', '1-10', '11-100', '101-1000', '1001+'] as const
const FILTER_BUCKETS = ['0', '1', '2', '3+'] as const
const ENTRY_SOURCES = [
  'campaign_banner',
  'product_list_banner',
  'coupon_list_button',
  'coupon_list_banner',
  'coupon_list_empty',
  'dashboard_perf',
  'dashboard_promo',
  'product_create_hook',
  'coupon_recreate', // S1 종료 쿠폰 "같은 조건으로 다시 등록"
  'direct',
] as const

// 공통 키 — 전 이벤트 허용. core 가 전역(gtag set)과 이벤트 파라미터 양쪽에 넣는다.
export const COMMON_KEYS = ['account_type', 'scope_seller_no', 'route_pattern', 'page_group', 'traffic_type'] as const
const COMMON: Record<string, Rule> = {
  account_type: { enum: ['seller', 'admin'] },
  scope_seller_no: { re: NO_RE },
  route_pattern: { route: true },
  page_group: { enum: ['대시보드', '상품', '주문', '프로모션', '문의리뷰', '정산', '통계', '공지', '설정', '도매', '시스템', '기타'] },
  traffic_type: { enum: ['internal'] },
}

const RULES: Record<AnalyticsEventName, Record<string, Rule>> = {
  page_view: {
    page_title: { label: true },
    page_location: { re: URL_RE },
    page_referrer: { re: URL_OR_EMPTY_RE },
    prev_route_pattern: { route: true },
  },
  login: { method: { enum: ['password', 'otp', 'admin', 'callback'] } },
  logout: { reason: { enum: ['user', 'session_expired', 'auth_error'] } },
  session_expired: {},
  seller_switch: { target_seller_no: { re: NO_RE } },
  access_denied: { denied_route_pattern: { route: true } },
  api_error: {
    endpoint: { re: ENDPOINT_RE },
    http_status: { int: true },
    error_code: { re: CODE_RE },
    api_group: { enum: ['seller', 'admin', 'order', 'adminapi', 'valhalla', 'mdm', 'other'] },
  },
  // menu_url: 라우트 템플릿 또는 비라우트 항목 고정값(/external = 외부 링크, /action = 다이얼로그·동작)
  menu_click: { menu_name: { menuLabel: true }, menu_url: { route: true, extra: ['/external', '/action'] }, depth: { int: true } },
  ui_click: {
    content_type: { enum: ['card', 'banner', 'button', 'link', 'modal'] },
    content_id: { re: CODE_RE },
    content_name: { menuLabel: true },
    index: { int: true },
  },
  outbound_click: { link_domain: { re: HOST_RE } },
  product_create: {
    result: { enum: RESULT },
    stage: { enum: ['validate', 'create', 'options_bulk', 'today_delivery', 'done'] },
    error_code: { re: CODE_RE },
    is_temp: { enum: YN },
    option_count: { enum: BUCKETS },
  },
  product_update: {
    result: { enum: RESULT },
    stage: { enum: ['validate', 'details', 'sales', 'options_bulk', 'done'] },
    section: { enum: ['details', 'sales', 'all'] },
    error_code: { re: CODE_RE },
    is_temp: { enum: YN },
  },
  product_soldout_apply: { result: { enum: RESULT }, error_code: { re: CODE_RE } },
  product_search: { filter_count: { enum: FILTER_BUCKETS }, has_keyword: { enum: YN } },
  product_bulk_edit: { result: { enum: RESULT }, error_code: { re: CODE_RE }, row_count: { enum: BUCKETS } },
  price_stock_update: {
    type: { enum: ['save', 'option_stock', 'bulk_discount', 'bulk_edit'] },
    result: { enum: RESULT },
    error_code: { re: CODE_RE },
  },
  option_editor_apply: { option_count: { enum: BUCKETS } },
  order_search: {
    list: { enum: ['order', 'cancel'] },
    preset: { enum: ['hold', 'delay', 'none'] },
    filter_count: { enum: FILTER_BUCKETS },
  },
  order_action: {
    action: {
      enum: [
        'ship_ready',
        'cancel',
        'delay',
        'delay_batch',
        'bundle',
        'complete',
        'back_to_ready',
        'confirm_hold',
        'confirm_approve',
        'product_ready',
        'return',
        'bank_verify',
        'refund',
        'refund_confirm',
        'refund_cancel',
        'cs',
        'redelivery',
      ],
    },
    source: { enum: ['list', 'detail'] },
    result: { enum: RESULT },
    error_code: { re: CODE_RE },
    count: { enum: BUCKETS },
  },
  invoice_register: {
    source: { enum: ['list_bulk', 'list_edit', 'detail', 'detail_edit', 'excel'] },
    result: { enum: RESULT },
    error_code: { re: CODE_RE },
  },
  coupon_form_view: { mode: { enum: ['create', 'update'] }, entry_source: { enum: ENTRY_SOURCES } },
  coupon_create: {
    mode: { enum: ['create', 'update'] },
    result: { enum: RESULT },
    error_code: { re: CODE_RE },
    entry_source: { enum: ENTRY_SOURCES },
  },
  coupon_validation_fail: {
    reason: {
      enum: [
        'name',
        'desc',
        'discount_price',
        'min_order_required',
        'min_order_lt_discount',
        'discount_rate',
        'max_discount_price',
        'min_order_price',
        'rate_cap',
        'download_period',
        'download_order',
        'download_end_limit',
        'valid_period',
        'valid_order',
        'download_after_valid',
        'valid_end_before_download',
        'valid_end_limit',
        'valid_days',
        'issue_count',
        'duplicate_conflict',
        'target_products',
        'download_window',
      ],
    },
  },
  coupon_end: { result: { enum: RESULT }, error_code: { re: CODE_RE } },
  campaign_apply_start: {},
  campaign_select: { campaign_no: { re: NO_RE } },
  campaign_apply: {
    source: { enum: ['form', 'excel'] },
    result: { enum: RESULT },
    error_code: { re: CODE_RE },
    product_count: { enum: BUCKETS },
    campaign_no: { re: NO_RE },
  },
  qna_answer: { action: { enum: ['create', 'update', 'delete'] }, result: { enum: RESULT }, error_code: { re: CODE_RE } },
  settings_update: {
    section: { enum: ['basic', 'shipping_policy', 'password', 'excel_password', 'docs'] },
    result: { enum: RESULT },
    error_code: { re: CODE_RE },
  },
  excel_download: {
    type: { enum: ['product', 'order', 'payout', 'ledger', 'vat', 'bulk_template', 'option'] },
    stage: { enum: ['request', 'file'] },
  },
}

// 라우트 템플릿 Set 밖이지만 page.ts 가 만드는 고정값
const SYNTHETIC_ROUTES = ['/not-found', '/unknown']

function applyRule(rule: Rule, raw: string | number | undefined, ctx: SanitizeCtx): string | number | undefined {
  if (raw === undefined || raw === null) return undefined
  if (rule.int) {
    const n = typeof raw === 'number' ? raw : Number(raw)
    return Number.isInteger(n) ? n : 0
  }
  const v = String(raw).slice(0, MAX_LEN)
  if (rule.route) {
    const extra = rule.extra || []
    return ctx.routePatterns.has(v) || SYNTHETIC_ROUTES.indexOf(v) >= 0 || extra.indexOf(v) >= 0 ? v : 'unknown'
  }
  if (rule.menuLabel) return ctx.labels.has(v) ? v : 'unknown'
  if (rule.enum) return rule.enum.indexOf(v) >= 0 ? v : 'unknown'
  if (rule.re) return rule.re.test(v) ? v : 'unknown'
  return v // label: 절단만 (라우트 meta.title 같은 정적 문자열 전용)
}

// 이벤트별 허용 키만 남기고 값을 검증한다. 절대 던지지 않는다.
export function sanitize(name: AnalyticsEventName, params: AnalyticsParams, ctx: SanitizeCtx): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  try {
    const rules = RULES[name] || {}
    const src = params || {}
    for (const key of Object.keys(src)) {
      const rule = rules[key] || COMMON[key]
      if (!rule) continue
      const v = applyRule(rule, src[key], ctx)
      if (v !== undefined) out[key] = v
    }
  } catch (e) {
    /* noop */
  }
  return out
}

export function bucket(n: number): typeof BUCKETS[number] {
  if (!n || n <= 0) return '0'
  if (n <= 10) return '1-10'
  if (n <= 100) return '11-100'
  if (n <= 1000) return '101-1000'
  return '1001+'
}

export function filterBucket(n: number): typeof FILTER_BUCKETS[number] {
  if (!n || n <= 0) return '0'
  if (n === 1) return '1'
  if (n === 2) return '2'
  return '3+'
}
